import * as DialogPrimitive from '@radix-ui/react-dialog'
import { AlertTriangle } from 'lucide-react'
import { Dialog, DialogOverlay, DialogPortal } from '@/components/ui/dialog'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { cn } from '@/utils'
import { useLiveAreaTopRight } from '@/hooks/use-live-area-top-right'
import type { PlacementWarningDialogProps } from './types/placement-warning-dialog-props'

type PlacementWarningDialogBaseProps = PlacementWarningDialogProps & {
  title: string
  message: string
  continueLabel: string
  /** `warning` is the orange "this will be adjusted" alert; `destructive` is the red "this will
   *  erase something" confirmation. */
  tone: 'warning' | 'destructive'
}

const TONE_STYLES = {
  warning: { icon: 'text-orange-600', title: 'text-orange-600', button: 'default' },
  destructive: { icon: 'text-red-700', title: 'text-red-700', button: 'destructive' },
} as const

/**
 * The top-right Cancel/Continue alert shown before a photo or ClikArt drop does something the
 * user may not expect (placing it in a new frame because it is too small; replacing text with it).
 * `PhotoSizeWarningAlert`, `TextToImageWarningDialog` and their ClikArt counterparts are thin
 * wrappers that supply the copy and tone.
 */
export function PlacementWarningDialog({
  open,
  onCancel,
  onContinue,
  title,
  message,
  continueLabel,
  tone,
}: PlacementWarningDialogBaseProps) {
  const { top, right } = useLiveAreaTopRight(open)
  const styles = TONE_STYLES[tone]

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onCancel()
      }}
    >
      <DialogPortal>
        {/* Overlay stays mounted (full-viewport, still captures/blocks outside pointer events and
            Radix's modal focus-trap) but transparent — no dimmed backdrop. */}
        <DialogOverlay className="bg-transparent" />
        <DialogPrimitive.Content
          className={cn(
            'fixed z-50 w-[calc(100%-24px)] max-w-2xl outline-none',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0'
          )}
          style={{ top, right }}
          // The only way out is Cancel or the confirm button — block every implicit Radix dismiss
          // path (outside click, outside focus, Escape) so the alert can't disappear without the
          // user making an explicit choice.
          onPointerDownOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => event.preventDefault()}
        >
          <Alert className="flex items-start gap-3 border-orange-300 bg-orange-50 p-3 shadow-lg">
            <span className="shrink-0">
              <AlertTriangle aria-hidden="true" className={cn('size-4', styles.icon)} />
            </span>
            <div className="min-w-0 flex-1">
              <DialogPrimitive.Title asChild>
                <AlertTitle className={styles.title}>{title}</AlertTitle>
              </DialogPrimitive.Title>
              <DialogPrimitive.Description asChild>
                <AlertDescription className="text-muted-foreground">{message}</AlertDescription>
              </DialogPrimitive.Description>
            </div>
            <div className="flex shrink-0 items-center gap-2 pl-2">
              <Button variant="outline" onClick={onCancel}>
                Cancel
              </Button>
              <Button variant={styles.button} onClick={onContinue}>
                {continueLabel}
              </Button>
            </div>
          </Alert>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  )
}
